import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { crmApi, errMsg } from '@crm/api';
import { canManageCrmAccess } from '@crm/permissions';
import { UiButton, UiCheckbox, UiInput } from '@shared/common/ui';

function IndeterminateCheckbox({ checked, indeterminate, disabled, onChange, ariaLabel }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return (
    <UiCheckbox
      ref={ref}
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={onChange}
      className="admin-access__checkbox"
    />
  );
}

function adminTitle(admin) {
  return admin.name || admin.login || 'Без имени';
}

export function SettingsAccessTab({ branchId }) {
  const { profile } = useSelector((s) => s.crmAuth);
  const canManage = canManageCrmAccess(profile);
  const [groups, setGroups] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [activeGroupKey, setActiveGroupKey] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    try {
      const crm = crmApi(branchId);
      const [{ data: registry }, { data: access }] = await Promise.all([
        crm.get('/admin-access/registry'),
        crm.get('/admin-access'),
      ]);
      const nextGroups = registry.groups ?? [];
      setGroups(nextGroups);
      setAdmins(access.admins ?? []);
      setActiveGroupKey((current) => current ?? nextGroups[0]?.key ?? null);
    } catch (err) {
      setError(errMsg(err, 'Не удалось загрузить доступы администраторов'));
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const activeGroup = useMemo(
    () => groups.find((group) => group.key === activeGroupKey) ?? groups[0] ?? null,
    [activeGroupKey, groups],
  );

  useEffect(() => {
    if (!activeGroupKey && groups[0]) {
      setActiveGroupKey(groups[0].key);
    }
  }, [activeGroupKey, groups]);

  const filteredAdmins = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return admins;
    return admins.filter((admin) =>
      `${admin.name ?? ''} ${admin.login ?? ''}`.toLowerCase().includes(normalized),
    );
  }, [admins, query]);

  const setAdminPermissions = useCallback((managerId, permissions) => {
    setAdmins((list) =>
      list.map((admin) => (admin.id === managerId ? { ...admin, permissions } : admin)),
    );
  }, []);

  const saveAdminPermissions = async (admin, nextPermissions, operationKey) => {
    const previous = admin.permissions ?? [];
    setSavingKey(operationKey);
    setNotice(null);
    setAdminPermissions(admin.id, nextPermissions);
    try {
      const { data } = await crmApi(branchId).put(`/admin-access/${admin.id}`, {
        permissions: nextPermissions,
      });
      setAdminPermissions(admin.id, data.permissions ?? nextPermissions);
      setNotice({ type: 'success', text: 'Права сохранены' });
    } catch (err) {
      setAdminPermissions(admin.id, previous);
      setNotice({ type: 'error', text: errMsg(err, 'Не удалось сохранить права') });
    } finally {
      setSavingKey(null);
    }
  };

  const onToggleAdminPermission = (admin, permissionKey, allowed) => {
    const set = new Set(admin.permissions ?? []);
    if (allowed) {
      set.add(permissionKey);
    } else {
      set.delete(permissionKey);
    }
    saveAdminPermissions(admin, Array.from(set), `${admin.id}:${permissionKey}`);
  };

  const onToggleColumn = async (permissionKey, allowed) => {
    const managerIds = admins.map((admin) => admin.id);
    if (managerIds.length === 0) return;

    const previous = admins;
    setSavingKey(`column:${permissionKey}`);
    setNotice(null);
    setAdmins((list) =>
      list.map((admin) => {
        const set = new Set(admin.permissions ?? []);
        if (allowed) {
          set.add(permissionKey);
        } else {
          set.delete(permissionKey);
        }
        return { ...admin, permissions: Array.from(set) };
      }),
    );

    try {
      await crmApi(branchId).patch('/admin-access/bulk', {
        managerIds,
        permissionKey,
        allowed,
      });
      setNotice({ type: 'success', text: 'Права сохранены' });
    } catch (err) {
      setAdmins(previous);
      setNotice({ type: 'error', text: errMsg(err, 'Не удалось сохранить права') });
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <div className="form-card form-card--wide">
        <div className="muted">Загрузка…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-card form-card--wide">
        <div className="form-error">{error}</div>
        <UiButton type="button" variant="ghost" onClick={load}>
          Повторить
        </UiButton>
      </div>
    );
  }

  const permissions = activeGroup?.permissions ?? [];

  return (
    <div className="admin-access">
      {notice && (
        <div className={`events-page__notice events-page__notice--${notice.type}`}>{notice.text}</div>
      )}

      <div className="admin-access__groups">
        {groups.map((group) => (
          <button
            key={group.key}
            type="button"
            className={group.key === activeGroup?.key ? 'active' : ''}
            onClick={() => setActiveGroupKey(group.key)}
          >
            {group.title}
          </button>
        ))}
      </div>

      <div className="form-card form-card--wide admin-access__toolbar">
        <UiInput
          label="Поиск администратора"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Имя или логин"
        />
        <div className="admin-access__toolbar-meta">
          {filteredAdmins.length} из {admins.length}
        </div>
      </div>

      <div className="form-card form-card--wide admin-access__matrix-card">
        {filteredAdmins.length === 0 ? (
          <div className="muted">Активные администраторы не найдены.</div>
        ) : (
          <div className="admin-access__table-wrap">
            <table className="admin-access__table">
              <thead>
                <tr>
                  <th className="admin-access__admin-col">Администратор</th>
                  {permissions.map((permission) => {
                    const checkedCount = admins.filter((admin) =>
                      (admin.permissions ?? []).includes(permission.key),
                    ).length;
                    const allChecked = admins.length > 0 && checkedCount === admins.length;
                    const partiallyChecked = checkedCount > 0 && !allChecked;
                    return (
                      <th key={permission.key}>
                        <div className="admin-access__permission-head">
                          <IndeterminateCheckbox
                            checked={allChecked}
                            indeterminate={partiallyChecked}
                            disabled={!canManage || savingKey === `column:${permission.key}`}
                            ariaLabel={`Изменить право ${permission.label} для всех`}
                            onChange={(e) => onToggleColumn(permission.key, e.target.checked)}
                          />
                          <span>{permission.label}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredAdmins.map((admin) => {
                  const adminPermissions = admin.permissions ?? [];
                  return (
                    <tr key={admin.id}>
                      <th scope="row" className="admin-access__admin-cell">
                        <span>{adminTitle(admin)}</span>
                        {admin.permissionPresetLabel ? (
                          <small className="admin-access__role">
                            Уровень доступа: {admin.permissionPresetLabel}
                          </small>
                        ) : null}
                        {admin.name && admin.login ? <small>{admin.login}</small> : null}
                      </th>
                      {permissions.map((permission) => {
                        const checked = adminPermissions.includes(permission.key);
                        const key = `${admin.id}:${permission.key}`;
                        return (
                          <td key={permission.key}>
                            <IndeterminateCheckbox
                              checked={checked}
                              disabled={!canManage || savingKey === key}
                              ariaLabel={`${adminTitle(admin)}: ${permission.label}`}
                              onChange={(e) =>
                                onToggleAdminPermission(admin, permission.key, e.target.checked)
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
